/**
 * Course to steer (CTS) — the tidal triangle.
 *
 * The problem we're solving: we want to make good a particular ground track
 * from A to B, but the tide is going to push us. What heading do we steer, and
 * how long will we take?
 *
 * The triangle:
 *   1. Draw the tidal vector (set/rate × time) from the start point.
 *   2. From the tip of the tidal vector, swing an arc of length (boat speed ×
 *      time) until it hits the desired ground track line.
 *   3. The line from the tip of the tidal vector to that intersection IS the
 *      course to steer (water track). Its bearing is the heading we steer.
 *   4. The point where the arc hits the ground track is where we will actually
 *      end up at the chosen time.
 *
 * In a one-hour version of the triangle (the usual paper-chart construction):
 *   tidal vector length = tidal rate in nm/hour
 *   boat-speed arc radius = boat speed through water in knots
 *
 * Solving analytically:
 *   Let α = angle between the tidal vector and the desired ground track.
 *   Let v_t = tidal rate, v_b = boat speed.
 *   Sine rule: sin(β)/v_t = sin(α)/v_b
 *     where β is the angle between CTS and the ground track.
 *   CTS bearing = (ground track bearing) ± β, choosing the sign that opposes
 *   the tide so we end up offsetting against the set.
 *
 * Wind leeway (optional) is applied as a constant offset once the water-track
 * bearing is solved. We use the SAME sign convention as the EP and gap tools:
 * +leeway means the water track lies to starboard of the heading, so the heading
 * to steer is (water track - leeway). Leeway estimation is up to the caller (see
 * leeway.ts) — this function takes a leeway angle in degrees.
 *
 * See docs/ardent_chart_plotter_brief.md §6.3.
 */
import { normaliseBearing, rhumbBearingDeg, rhumbDestination, rhumbDistanceNm } from './geodesy';

const D2R = Math.PI / 180;
const R2D = 180 / Math.PI;

export interface CtsInput {
  /** Start position [lon, lat]. */
  from: [number, number];
  /** Destination position [lon, lat] — defines the desired ground track. */
  to: [number, number];
  /** Tidal set: the direction the tide flows TOWARDS, in degrees true. */
  tidalSet: number;
  /** Tidal rate in knots. */
  tidalRate: number;
  /** Boat speed through water in knots. */
  boatSpeed: number;
  /**
   * Optional leeway angle in degrees, same convention as the EP and gap tools:
   * positive means the water track sits to STARBOARD of the heading (a wind from
   * port pushing the boat's head off to starboard). To make good the solved water
   * track we steer to port of it, so the heading is (water track - leeway).
   * Negative for a starboard wind. If you don't know yet, leave it 0.
   */
  leewayDeg?: number;
}

export interface CtsResult {
  /** Course to steer (compass heading), degrees true. Includes any leeway offset. */
  ctsDeg: number;
  /** The water track bearing (what you'd see on a chartplotter as heading). */
  waterTrackDeg: number;
  /** Speed over ground, knots, given the tide does what it says. */
  sogKnots: number;
  /** Time to reach the destination at this CTS, in hours. */
  hours: number;
  /** Ground track bearing (a → b). */
  groundTrackDeg: number;
  /** Distance to run from a → b, in nm. */
  distanceNm: number;
  /**
   * True when the tide is too strong relative to the boat speed to make the
   * desired ground track at all (mathematically: sin(β) > 1). The chartwork
   * panel should surface this rather than silently produce a NaN.
   */
  unachievable: boolean;
}

/**
 * Solve the CTS triangle.
 *
 * The maths is the standard sine-rule construction taught on Day Skipper /
 * Yachtmaster theory. We don't approximate or interpolate — for the small
 * distances of an exercise leg, plane trig on bearings is the right tool.
 */
export function courseToSteer(input: CtsInput): CtsResult {
  const distanceNm = rhumbDistanceNm(input.from, input.to);
  const groundTrack = rhumbBearingDeg(input.from, input.to);

  // Angle between the tidal vector and the ground track. Positive => tide
  // pushing us off the track to one side; the sign tells us which.
  const trackToTide = normaliseBearing(input.tidalSet - groundTrack);
  // Convert into [-180, 180] so we can talk about left/right of track.
  const alpha = trackToTide > 180 ? trackToTide - 360 : trackToTide;

  // From the sine rule applied to the tidal triangle:
  //   sin(β) / v_t = sin(angle between tide and water track) / v_b
  // For the unit (one-hour) construction, the tip of the tide vector lands at
  // distance v_t from the start, and the boat's water-track vector has length
  // v_b. Solving for β (the offset between CTS and ground track):
  const sinBeta = (input.tidalRate * Math.sin(alpha * D2R)) / input.boatSpeed;
  if (Math.abs(sinBeta) > 1) {
    return {
      ctsDeg: NaN,
      waterTrackDeg: NaN,
      sogKnots: NaN,
      hours: NaN,
      groundTrackDeg: groundTrack,
      distanceNm,
      unachievable: true,
    };
  }
  const beta = Math.asin(sinBeta) * R2D;

  // CTS opposes the tide: if the tide is pushing us right of the track (alpha
  // positive), we steer left of the track (subtract beta), and vice versa.
  // Beta as computed already carries the correct sign through sin(alpha), so:
  const waterTrack = normaliseBearing(groundTrack - beta);

  // Speed over ground via the third side of the triangle (cosine rule).
  // The triangle has sides: v_b (CTS), v_t (tide), v_g (SOG). The angle
  // opposite v_g is the angle between the tidal vector and the water track.
  // gamma = 180 - alpha - (-beta) = 180 - alpha + beta in absolute terms.
  const gamma = 180 - Math.abs(alpha) - Math.abs(beta);
  const sogKnots = Math.sqrt(
    input.boatSpeed * input.boatSpeed +
      input.tidalRate * input.tidalRate -
      2 * input.boatSpeed * input.tidalRate * Math.cos(gamma * D2R),
  );

  const hours = sogKnots > 0 ? distanceNm / sogKnots : Infinity;

  // heading = water track - leeway (matches estimatedPosition / gap.ts, where
  // +leeway puts the water track to starboard of the heading). Steering
  // waterTrack + leeway offsets the wrong way and puts the CTS out by twice the
  // leeway angle against the EP tool.
  const cts = normaliseBearing(waterTrack - (input.leewayDeg ?? 0));

  return {
    ctsDeg: cts,
    waterTrackDeg: waterTrack,
    sogKnots,
    hours,
    groundTrackDeg: groundTrack,
    distanceNm,
    unachievable: false,
  };
}

/**
 * Dead reckoning: from a known position at time t0, steering a constant
 * heading at constant speed, where are we after `hours`?
 *
 * Pure rhumb-line walk. No tide or leeway applied.
 */
export function deadReckon(
  from: [number, number],
  headingDeg: number,
  speedKnots: number,
  hours: number,
): [number, number] {
  return rhumbDestination(from, headingDeg, speedKnots * hours);
}

/**
 * Estimated position: dead-reckoning position plus the tidal vector accumulated
 * over the same period, plus any leeway offset on the heading.
 *
 * Leeway is taken as an angle that the *water track* differs from the heading
 * — wind from starboard pushes the boat to port, so the water track is a few
 * degrees to port of the heading.
 */
export function estimatedPosition(input: {
  from: [number, number];
  headingDeg: number;
  speedKnots: number;
  hours: number;
  tidalSet: number;
  tidalRate: number;
  leewayDeg?: number;
}): {
  dr: [number, number];
  ep: [number, number];
} {
  const waterTrack = normaliseBearing(input.headingDeg + (input.leewayDeg ?? 0));
  const dr = rhumbDestination(input.from, waterTrack, input.speedKnots * input.hours);
  const ep = rhumbDestination(dr, input.tidalSet, input.tidalRate * input.hours);
  return { dr, ep };
}
